<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="mb-4">
          <a href="/admin/prompt_templates" class="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            &larr; Back to Prompt Templates
          </a>
        </div>
        <div class="flex items-start justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {{ template.name }}
            </h1>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 mr-2">
                {{ template.category }}
              </span>
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 mr-2">
                {{ template.role }}
              </span>
              <span class="text-gray-400 dark:text-gray-500">
                v{{ template.version }} &middot; {{ template.slug }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- Editor (2 cols) -->
        <div class="md:col-span-2 space-y-6">
          <!-- Body textarea -->
          <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <label class="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Prompt Body
            </label>
            <textarea
              ref="bodyTextarea"
              v-model="form.body"
              rows="20"
              class="w-full font-mono text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
              placeholder="Enter prompt template body..."
            ></textarea>
          </div>

          <!-- Description -->
          <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <label class="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Description
            </label>
            <textarea
              v-model="form.description"
              rows="3"
              class="w-full text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
              placeholder="Describe what this template does..."
            ></textarea>
          </div>

          <!-- Preview -->
          <div v-if="showPreview" class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
              <i class="fas fa-eye mr-1"></i> Preview
            </h3>
            <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">{{ renderedPreview }}</div>
          </div>

          <!-- Actions -->
          <div class="flex items-center space-x-3">
            <button
              @click="save"
              :disabled="saving"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed">
              <i class="fas fa-save mr-2"></i>
              {{ saving ? 'Saving...' : 'Save Changes' }}
            </button>
            <button
              @click="togglePreview"
              class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
              <i class="fas fa-eye mr-2"></i>
              {{ showPreview ? 'Hide Preview' : 'Show Preview' }}
            </button>
            <transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition ease-in duration-150"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0">
              <span v-if="feedbackMessage" :class="feedbackClass" class="text-sm font-medium ml-2">
                {{ feedbackMessage }}
              </span>
            </transition>
          </div>
        </div>

        <!-- Sidebar (1 col) -->
        <div class="md:col-span-1 space-y-6">
          <!-- Variables panel -->
          <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
              <i class="fas fa-code mr-1"></i> Variables
            </h3>
            <div v-if="variables.length > 0" class="space-y-2">
              <div v-for="v in variables" :key="v.name">
                <button
                  @click="insertVariable(v.name)"
                  class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors cursor-pointer">
                  {{ wrapVar(v.name) }}
                </button>
                <p v-if="v.description" class="mt-0.5 ml-1 text-xs text-gray-500 dark:text-gray-400">
                  {{ v.description }}
                </p>
              </div>
            </div>
            <p v-else class="text-sm text-gray-500 dark:text-gray-400">
              No variables detected. Use <code class="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded" v-text="wrapVar('variable_name')"></code> syntax in the body.
            </p>
          </div>

          <!-- Template metadata -->
          <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
              <i class="fas fa-info-circle mr-1"></i> Metadata
            </h3>
            <dl class="space-y-3">
              <div>
                <dt class="text-xs text-gray-500 dark:text-gray-400">Slug</dt>
                <dd class="text-sm font-mono text-gray-900 dark:text-gray-100">{{ template.slug }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500 dark:text-gray-400">Category</dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">{{ template.category }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500 dark:text-gray-400">Role</dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">{{ template.role }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500 dark:text-gray-400">Version</dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">{{ template.version }}</dd>
              </div>
              <div v-if="template.updated_at">
                <dt class="text-xs text-gray-500 dark:text-gray-400">Last Updated</dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">{{ formatDate(template.updated_at) }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { hideLoadingSpinner } from '~/javascript/utils/hide_loader.js'
import { toastSuccess, toastError } from '~/javascript/utils/toast.js'
import securedAxios from '~/javascript/utils/axios.js'

const axios = securedAxios()

const props = defineProps({
  template: { type: Object, required: true }
})

const form = ref({
  body: '',
  description: ''
})

const saving = ref(false)
const showPreview = ref(false)
const feedbackMessage = ref('')
const feedbackClass = ref('')
const bodyTextarea = ref(null)

// Extract {{variables}} from the body text
const variables = computed(() => {
  const matches = form.value.body.match(/\{\{(\w+)\}\}/g)
  if (!matches) return []
  const unique = [...new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, '')))]
  const descriptions = props.template.variable_descriptions || {}
  return unique.map((name) => ({
    name,
    description: descriptions[name] || null
  }))
})

// Render preview with sample data filled in
const renderedPreview = computed(() => {
  let text = form.value.body
  variables.value.forEach((v) => {
    const sampleValue = `[SAMPLE_${v.name.toUpperCase()}]`
    const regex = new RegExp(`\\{\\{${v.name}\\}\\}`, 'g')
    text = text.replace(regex, sampleValue)
  })
  return text
})

function wrapVar(name) {
  return '{' + '{' + name + '}' + '}'
}

function insertVariable(varName) {
  const textarea = bodyTextarea.value
  if (!textarea) return

  const insertion = `{{${varName}}}`
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const before = form.value.body.substring(0, start)
  const after = form.value.body.substring(end)

  form.value.body = before + insertion + after

  // Restore cursor position after insertion
  const cursorPos = start + insertion.length
  requestAnimationFrame(() => {
    textarea.focus()
    textarea.setSelectionRange(cursorPos, cursorPos)
  })
}

function togglePreview() {
  showPreview.value = !showPreview.value
}

function showFeedback(message, type) {
  feedbackMessage.value = message
  feedbackClass.value = type === 'success'
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400'
  setTimeout(() => {
    feedbackMessage.value = ''
  }, 3000)
}

async function save() {
  try {
    saving.value = true
    await axios.patch(`/ajax/admin/prompt_templates/${props.template.id}`, {
      prompt_template: {
        body: form.value.body,
        description: form.value.description
      }
    })
    toastSuccess('Template saved successfully')
    showFeedback('Saved!', 'success')
  } catch (error) {
    toastError(error)
    showFeedback('Save failed', 'error')
  } finally {
    saving.value = false
  }
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

onMounted(() => {
  hideLoadingSpinner()
  form.value.body = props.template.body || ''
  form.value.description = props.template.description || ''
})
</script>
